import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import type { User } from "firebase/auth";

import { db } from "../firebase/client";

export type UsagePlanTier = "free" | "trial" | "pro" | "premium";
export type UsageMetric =
  | "analysesMonthly"
  | "ocrMonthly"
  | "threeDMonthly"
  | "patients";

export type UsageLimits = Record<UsageMetric, number | null>;

export type UsageState = {
  usageMonth: string;
  analysesMonthly: number;
  ocrMonthly: number;
  threeDMonthly: number;
  patients: number;
  updatedAt?: unknown;
};

export const PLAN_USAGE_LIMITS: Record<UsagePlanTier, UsageLimits> = {
  free: {
    analysesMonthly: 5,
    ocrMonthly: 3,
    threeDMonthly: 3,
    patients: 3,
  },
  trial: {
    analysesMonthly: 50,
    ocrMonthly: 50,
    threeDMonthly: 50,
    patients: 100,
  },
  pro: {
    analysesMonthly: 30,
    ocrMonthly: 30,
    threeDMonthly: 30,
    patients: null,
  },
  premium: {
    analysesMonthly: 100,
    ocrMonthly: 100,
    threeDMonthly: 100,
    patients: null,
  },
};

export class UsageLimitError extends Error {
  metric: UsageMetric;
  limit: number | null;
  used: number;
  requested: number;
  tier: UsagePlanTier;

  constructor(params: {
    metric: UsageMetric;
    limit: number | null;
    used: number;
    requested: number;
    tier: UsagePlanTier;
  }) {
    super(`Usage limit exceeded for ${params.metric}`);
    this.name = "UsageLimitError";
    this.metric = params.metric;
    this.limit = params.limit;
    this.used = params.used;
    this.requested = params.requested;
    this.tier = params.tier;
  }
}

export function getCurrentUsageMonthKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export function normalizeUsagePlanTier(value: unknown): UsagePlanTier {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (normalized === "trial") return "trial";
  if (normalized === "basic") return "pro"; // legado: basic → pro
  if (normalized === "pro") return "pro";
  if (normalized === "premium") return "premium";
  return "free";
}

function toMillisFlexible(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const p = Date.parse(value);
    return Number.isNaN(p) ? null : p;
  }
  if (value && typeof value === "object") {
    const ts = value as { toMillis?: () => number; seconds?: unknown };
    if (typeof ts.toMillis === "function") return ts.toMillis();
    const sec = Number((ts as any).seconds);
    if (Number.isFinite(sec)) return sec * 1000;
  }
  return null;
}

function normalizeLimitValue(value: unknown): number | null {
  if (value == null) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  if (parsed >= 9999) return null;
  if (parsed < 0) return 0;
  return parsed;
}

function normalizeUsageNumber(value: unknown) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed);
}

export function getPlanUsageLimits(tier: UsagePlanTier): UsageLimits {
  return PLAN_USAGE_LIMITS[tier];
}

export function buildInitialUsageState(
  patientCount = 0,
  date = new Date()
): UsageState {
  return {
    usageMonth: getCurrentUsageMonthKey(date),
    analysesMonthly: 0,
    ocrMonthly: 0,
    threeDMonthly: 0,
    patients: Math.max(0, Math.floor(patientCount)),
  };
}

export function normalizeUsageState(raw: any, date = new Date()): UsageState {
  const base = buildInitialUsageState(0, date);
  const next: UsageState = {
    usageMonth: String(raw?.usageMonth || base.usageMonth),
    analysesMonthly: normalizeUsageNumber(raw?.analysesMonthly),
    ocrMonthly: normalizeUsageNumber(raw?.ocrMonthly),
    threeDMonthly: normalizeUsageNumber(raw?.threeDMonthly),
    patients: normalizeUsageNumber(raw?.patients),
    updatedAt: raw?.updatedAt,
  };

  if (next.usageMonth !== base.usageMonth) {
    return {
      ...next,
      usageMonth: base.usageMonth,
      analysesMonthly: 0,
      ocrMonthly: 0,
      threeDMonthly: 0,
    };
  }

  return next;
}

export function resolveUsageLimits(raw: any, fallbackTier?: unknown): UsageLimits {
  const UNLIMITED: UsageLimits = { analysesMonthly: null, ocrMonthly: null, threeDMonthly: null, patients: null };

  if (
    raw?.role === "admin" ||
    raw?.adminaccessLevel === "admin" ||
    raw?.isPremium === true
  ) {
    return UNLIMITED;
  }

  let tier = normalizeUsagePlanTier(
    raw?.plan?.tier ?? raw?.planTier ?? raw?.tier ?? raw?.role ?? fallbackTier
  );

  // Se trial expirado, tratar como free para limites
  if (tier === "trial") {
    const expiresAt = raw?.trialExpiresAt ?? raw?.trialEndsAt ?? null;
    const expiresMs = toMillisFlexible(expiresAt);
    if (expiresMs !== null && expiresMs < Date.now()) {
      tier = "free";
    }
  }

  const defaults = getPlanUsageLimits(tier);

  // If no explicit limits document, use plan defaults
  const explicitLimits = raw?.limits;
  const hasExplicitLimitsDoc = explicitLimits != null && typeof explicitLimits === "object";

  // Key explicitly present in Firestore (even as null) → null means unlimited.
  // Key absent → fall back to plan defaults.
  const resolve = (key: keyof UsageLimits): number | null => {
    if (hasExplicitLimitsDoc && key in explicitLimits) {
      return normalizeLimitValue(explicitLimits[key]);
    }
    return defaults[key];
  };

  return {
    analysesMonthly: resolve("analysesMonthly"),
    ocrMonthly: resolve("ocrMonthly"),
    threeDMonthly: resolve("threeDMonthly"),
    patients: resolve("patients"),
  };
}

export function getRemainingUsage(limits: UsageLimits, usage: UsageState) {
  return {
    analysesMonthly:
      limits.analysesMonthly == null
        ? null
        : Math.max(0, limits.analysesMonthly - usage.analysesMonthly),
    ocrMonthly:
      limits.ocrMonthly == null
        ? null
        : Math.max(0, limits.ocrMonthly - usage.ocrMonthly),
    threeDMonthly:
      limits.threeDMonthly == null
        ? null
        : Math.max(0, limits.threeDMonthly - usage.threeDMonthly),
    patients:
      limits.patients == null
        ? null
        : Math.max(0, limits.patients - usage.patients),
  };
}

function buildUsagePatch(usage: UsageState) {
  return {
    ...usage,
    updatedAt: serverTimestamp(),
  };
}

async function mutateUsageState<T>(
  userId: string,
  mutate: (ctx: {
    usage: UsageState;
    tier: UsagePlanTier;
    limits: UsageLimits;
    raw: Record<string, any>;
  }) => { usage: UsageState; limits?: UsageLimits; result: T; skipWrite?: boolean }
): Promise<T> {
  const userRef = doc(db, "users", userId);

  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(userRef);
    if (!snap.exists()) {
      throw new Error("Perfil do usuario nao encontrado para controlar o uso.");
    }

    const raw = (snap.data() || {}) as Record<string, any>;
    const tier = normalizeUsagePlanTier(
      raw?.plan?.tier ?? raw?.planTier ?? raw?.tier ?? raw?.role
    );
    const limits = resolveUsageLimits(raw, tier);
    const usage = normalizeUsageState(raw?.usage);
    const next = mutate({ usage, tier, limits, raw });

    if (!next.skipWrite) {
      const patch: Record<string, unknown> = {
        usage: buildUsagePatch(next.usage),
        updatedAt: serverTimestamp(),
      };
      // Only write limits back if the mutate callback explicitly changed them.
      // Never write resolved/computed limits — they would overwrite null (unlimited)
      // values in Firestore with the plan default (e.g. 100), permanently revoking
      // the user's unlimited status.
      if (next.limits !== undefined) {
        patch.limits = next.limits;
      }
      transaction.set(userRef, patch, { merge: true });
    }

    return next.result;
  });
}

export async function consumeUsageQuota(
  userId: string,
  metric: UsageMetric,
  amount = 1
) {
  return mutateUsageState(userId, ({ usage, tier, limits, raw }) => {
    const isAdmin =
      raw?.role === "admin" ||
      raw?.adminaccessLevel === "admin" ||
      raw?.isPremium === true;

    if (isAdmin) {
      return {
        usage,
        skipWrite: true,
        result: {
          tier,
          limits,
          usage,
          remaining: getRemainingUsage(limits, usage),
        },
      };
    }

    const current = usage[metric];
    const limit = limits[metric];

    if (limit != null && current + amount > limit) {
      throw new UsageLimitError({
        metric,
        limit,
        used: current,
        requested: amount,
        tier,
      });
    }

    const nextUsage = {
      ...usage,
      [metric]: current + amount,
    };

    return {
      usage: nextUsage,
      result: {
        tier,
        limits,
        usage: nextUsage,
        remaining: getRemainingUsage(limits, nextUsage),
      },
    };
  });
}

export async function adjustUsageQuota(
  userId: string,
  metric: UsageMetric,
  delta: number
) {
  return mutateUsageState(userId, ({ usage, tier, limits }) => {
    const nextUsage = {
      ...usage,
      [metric]: Math.max(0, usage[metric] + delta),
    };

    return {
      usage: nextUsage,
      result: {
        tier,
        limits,
        usage: nextUsage,
        remaining: getRemainingUsage(limits, nextUsage),
      },
    };
  });
}

export async function syncPatientUsageCount(userId: string, patientCount: number) {
  return mutateUsageState(userId, ({ usage, tier, limits }) => {
    if (usage.patients === patientCount) {
      return {
        usage,
        skipWrite: true,
        result: {
          tier,
          limits,
          usage,
          remaining: getRemainingUsage(limits, usage),
        },
      };
    }

    const nextUsage = {
      ...usage,
      patients: Math.max(0, Math.floor(patientCount)),
    };

    return {
      usage: nextUsage,
      result: {
        tier,
        limits,
        usage: nextUsage,
        remaining: getRemainingUsage(limits, nextUsage),
      },
    };
  });
}

export function getUsageTierForUser(user: User | null, fallbackTier?: unknown) {
  return normalizeUsagePlanTier(fallbackTier);
}
